import { useEffect, useState } from 'react';
import { getEmailSettingsStatus, sendEmailTest } from '../../../api/adminEmailCenter';
import EmailCenterPage, { PageSection, StatCard, TextAreaField, TextField } from './EmailCenterShared';

export default function EmailSettings() {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({
    recipientEmail: '',
    subject: 'HomeVilas admin test email',
    htmlBody: '<div><h1>HomeVilas test</h1><p>This is an admin-triggered SMTP test.</p></div>',
    textBody: 'HomeVilas admin SMTP test',
  });
  const [state, setState] = useState({ loading: true, sending: false, error: '', info: '' });

  const load = async () => {
    setState((current) => ({ ...current, loading: true, error: '' }));
    try {
      const data = await getEmailSettingsStatus();
      setStatus(data);
      setState((current) => ({ ...current, loading: false }));
    } catch (error) {
      setState((current) => ({ ...current, loading: false, error: error.message || 'Failed to load settings status' }));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async () => {
    setState((current) => ({ ...current, sending: true, error: '', info: '' }));
    try {
      await sendEmailTest(form);
      setState((current) => ({ ...current, sending: false, info: `Test email sent to ${form.recipientEmail}` }));
      await load();
    } catch (error) {
      setState((current) => ({ ...current, sending: false, error: error.message || 'Test send failed' }));
    }
  };

  return (
    <EmailCenterPage title="Settings & SMTP Status" description="Backend-only SMTP health, queue runtime settings, and a safe admin test-send surface.">
      {state.error ? <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-700">{state.error}</div> : null}
      {state.info ? <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{state.info}</div> : null}

      <div className="grid gap-4 xl:grid-cols-4 md:grid-cols-2">
        <StatCard label="SMTP Configured" value={status?.smtp?.configured ? 'Yes' : 'No'} hint="Secrets stay backend-only." tone={status?.smtp?.configured ? 'emerald' : 'rose'} />
        <StatCard label="Batch Size" value={status?.worker?.batchSize || 0} hint="Messages processed per queue batch." tone="sky" />
        <StatCard label="Max Retry" value={status?.worker?.maxRetry || 0} hint="Retry cap for failed messages." tone="amber" />
        <StatCard label="Last Test" value={status?.lastTest?.status || 'N/A'} hint={status?.lastTest?.at || 'No test run yet'} />
      </div>

      <div className="grid gap-6 xl:grid-cols-[0.8fr_1.2fr]">
        <PageSection title="SMTP Health" subtitle="This view exposes configuration state only, never SMTP secrets.">
          <div className="space-y-3 rounded-[24px] border border-slate-100 bg-slate-50/60 p-4 text-sm text-slate-600">
            <p><strong>Host configured:</strong> {status?.smtp?.hostConfigured ? 'Yes' : 'No'}</p>
            <p><strong>Port configured:</strong> {status?.smtp?.portConfigured ? 'Yes' : 'No'}</p>
            <p><strong>User configured:</strong> {status?.smtp?.userConfigured ? 'Yes' : 'No'}</p>
            <p><strong>TLS server name configured:</strong> {status?.smtp?.tlsServerNameConfigured ? 'Yes' : 'No'}</p>
            <p><strong>Secure transport:</strong> {status?.smtp?.secure ? 'True' : 'False'}</p>
            <p><strong>Last worker run:</strong> {status?.worker?.lastRunAt || 'N/A'}</p>
            <p><strong>Worker note:</strong> {status?.worker?.lastRunNote || 'N/A'}</p>
          </div>
        </PageSection>

        <PageSection title="Send Test Email" subtitle="Validate rendering and SMTP delivery before launching a campaign.">
          <div className="grid gap-4 md:grid-cols-2">
            <TextField label="Recipient Email" value={form.recipientEmail} onChange={(e) => setForm((current) => ({ ...current, recipientEmail: e.target.value }))} />
            <TextField label="Subject" value={form.subject} onChange={(e) => setForm((current) => ({ ...current, subject: e.target.value }))} />
            <div className="md:col-span-2">
              <TextAreaField label="HTML Body" value={form.htmlBody} onChange={(e) => setForm((current) => ({ ...current, htmlBody: e.target.value }))} className="min-h-[220px] font-mono" />
            </div>
            <div className="md:col-span-2">
              <TextAreaField label="Text Body" value={form.textBody} onChange={(e) => setForm((current) => ({ ...current, textBody: e.target.value }))} className="min-h-[120px] font-mono" />
            </div>
          </div>
          <div className="mt-4">
            <button type="button" onClick={submit} disabled={state.sending} className="rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white">
              {state.sending ? 'Sending...' : 'Send Test Email'}
            </button>
          </div>
        </PageSection>
      </div>
    </EmailCenterPage>
  );
}
